import { useEffect, useState, useRef } from "react";
import {
  IonContent,
  IonPage,
  IonIcon,
  useIonModal,
  IonItem,
  IonList,
  useIonToast,
  IonRadioGroup,
  IonRadio,
  IonProgressBar,
  useIonAlert,
  isPlatform,
} from "@ionic/react";
import Button from "../../components/atoms/button";
import deleteSvg from "../../images/delete.svg";
import { Formik, Field } from "formik";
import { chevronBackOutline } from "ionicons/icons";
import {
  onFileUploadValidationSize,
  CreateNhcIntegration,
  deleteNhcIntegration,
  CheckNhcIntegration,
  GetPointData,
  GetTypeOfProperty,
  GetSimilarAds,
} from "../../services/API";
import { PointDataEnvelope, Result as PointDataResult } from "../../services/models/PointDataResponse";
import { useSelector } from "react-redux";
import ErrorModal from "../../components/molecules/errorModal";
import { OverlayEventDetail } from "@ionic/react/dist/types/components/react-component-lib/interfaces";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Request from "@arcgis/core/request";
import { arcgisServicesLandFeatureUrl } from "../../configs/arcgisServices";
import Spinner from "../../components/atoms/spinner";
import {
  convertToBase64,
  determineParcelStatus,
  getAdvertisementTypeId,
  presentToastFunction,
} from "../../services/genericMethoud/generice";
import {
  favoriteLandSymbol,
  requestOptions,
  selectedSymbolElshamalya,
  getPolygonCenterPoint,
  getLandAdsByCoords,
} from "../../services/genericMethoud/gisMethouds";
import Query from "@arcgis/core/rest/support/Query";
import { executeQueryJSON } from "@arcgis/core/rest/query";
import Graphic from "@arcgis/core/Graphic";
import Point from "@arcgis/core/geometry/Point";
import Polygon from "@arcgis/core/geometry/Polygon";
import "./nhcIntegration.style.css";
import { getBaladyAccessToken } from "../../services/API/baladyService";
import EditProfile from "../editProfile/index";

interface FileWithProgress {
  file: File;
  progress: number;
  url: string | null;
  prepared: boolean;
}

const AddPropertyNhcIntegration = ({
  dismisAddProperty,
}: {
  dismisAddProperty: (
    data?: string | null | undefined | number,
    role?: string
  ) => void;
}) => {
  const { token } = useSelector((state: any) => state.reducer);
  const { userInfo } = useSelector((state: any) => state.reducer);
  const [errorTitle, setErrorTitle] = useState("");
  const [addIDType, setAddIDType] = useState("1");
  const [errorMessage, setErrorMessage] = useState("");
  const [parcel_Cod, setParcel_Cod] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPhoneWarning, setShowPhoneWarning] = useState(false);
  const inputClassWithOutError =
    "block px-5 py-2.5 font-bold placeholder-paragraphColor w-full text-xl  bg-transparent rounded border-2 border-paragraphColor appearance-none  focus:outline-none focus:ring-0 focus:border-blue-600";
  const [presentToast] = useIonToast();
  const [files, setFiles] = useState<FileWithProgress[]>([]);
  const [presentAlert] = useIonAlert();
  const isSubmittingRef = useRef(false);
  const formikRef = useRef<any>(null);

  // Check if user has phone number on mount
  useEffect(() => {
    console.log(userInfo);
    
    if (!userInfo?.phoneNumber || userInfo.phoneNumber === "") {
      setShowPhoneWarning(true);
    }
  }, [userInfo]);

  // Trigger validation when addIDType changes
  useEffect(() => {
    if (formikRef.current) {
      formikRef.current.validateForm();
    }
  }, [addIDType]);

  // Generic helper to delete GIS features by query
  const deleteGisFeaturesByQuery = async (
    featureLayer: any,
    query: any
  ): Promise<void> => {
    const result = await featureLayer.queryFeatures(query);
    
    if (result.features.length > 0) {
      const deleteFeatures = result.features.map((element: any) => ({
        objectId: element.attributes.OBJECTID,
      }));

      await featureLayer.applyEdits({ deleteFeatures });
      featureLayer.refresh();
    }
  };

  // Helper function to rollback or update GIS data when backend fails
  const rollbackGisData = async (objectId: number, adLicenseNumber: string) => {
    try {
      // Step 1: Check backend for existing ads with the same license number
      console.log("Checking backend for existing ads with license:", adLicenseNumber);
      
      let backendAds: any[] = [];
      try {
        const adsResponse = await GetSimilarAds(token, undefined, objectId);
        backendAds = adsResponse?.data || [];
        console.log("Backend ads count:", backendAds.length);
      } catch (error) {
        console.error("Error fetching ads from backend:", error);
        // If error or ads > 1, don't rollback
        setIsLoading(false);
        return;
      }

      // Step 2: Decide action based on ads count
      if (backendAds.length > 1) {
        // Don't rollback if multiple ads exist
        console.log("Multiple ads exist, skipping GIS rollback");
        setIsLoading(false);
        return;
      }

      if (backendAds.length === 1) {
        // Update GIS with the remaining ad's status
        console.log("One ad exists, updating GIS attributes");
        
        const remainingAd = backendAds[0];
        const newParcelStatus = remainingAd.landTypeDesc;
        const newBaseLandType = getAdvertisementTypeId(newParcelStatus);
        
        console.log("Updating to Parcel_Status:", newParcelStatus);
        console.log("Updating to BaseLandType:", newBaseLandType);

        // Update Layer 1 (F_Land_parcels)
        const updateFeature = {
          attributes: {
            OBJECTID: objectId,
            Parcel_Status: newParcelStatus,
            BaseLandType: newBaseLandType,
          },
        };

        const updateResult = await window.F_Land_parcels.applyEdits({
          updateFeatures: [updateFeature],
        });

        if (updateResult.updateFeatureResults[0].error == null) {
          window.F_Land_parcels.refresh();

          // Update Layer 2 (F_Land_parcelsHeatmap)
          const heatmapQuery = new Query({
            returnGeometry: false,
            where: `Parcel_Cod = '${objectId}'`,
            outFields: ["*"],
          });

          const heatmapFeatures = await window.F_Land_parcelsHeatmap.queryFeatures(
            heatmapQuery
          );

          if (heatmapFeatures.features.length > 0) {
            const heatmapUpdate = {
              attributes: {
                OBJECTID: heatmapFeatures.features[0].attributes.OBJECTID,
                Parcel_Status: newParcelStatus,
                BaseLandType: newBaseLandType,
              },
            };

            await window.F_Land_parcelsHeatmap.applyEdits({
              updateFeatures: [heatmapUpdate],
            });
            window.F_Land_parcelsHeatmap.refresh();
          }

        } else {
          setErrorTitle("خطأ في تحديث البيانات الجغرافية");
          setErrorMessage("فشل في تحديث حالة القطعة");
          openpErrorModal();
        }

        setIsLoading(false);
        return;
      }

      if (backendAds.length === 0) {
        // No ads exist, rollback (delete) GIS data
        console.log("No ads exist, rolling back GIS data");
        
        // Query main layer by OBJECTID
        const mainQuery = new Query({
          returnGeometry: false,
          objectIds: [objectId],
          outFields: ["*"],
        });

        // Query heatmap layer by Parcel_Cod
        const heatmapQuery = new Query({
          returnGeometry: false,
          where: `Parcel_Cod = '${objectId}'`,
          outFields: ["*"],
        });

        // Delete from F_Land_parcels
        await deleteGisFeaturesByQuery(window.F_Land_parcels, mainQuery);

        // Delete from F_Land_parcelsHeatmap
        await deleteGisFeaturesByQuery(window.F_Land_parcelsHeatmap, heatmapQuery);

        presentToastFunction(
          presentToast,
          "top",
          "danger",
          "تم التراجع عن العملية - يمكنك المحاولة مرة أخرى"
        );
        setIsLoading(false);
        return;
      }
    } catch (e) {
      console.error("GIS Rollback/Update failed:", e);
      setErrorTitle("خطأ حرج");
      setErrorMessage("فشل التراجع عن البيانات الجغرافية - يرجى التواصل مع الدعم");
      openpErrorModal();
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log("files state updated", files);
  }, [files]);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedFiles = Array.from(event.target.files || []);
    const maxFileSizeMB = 6;
    const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;

    const newFiles: FileWithProgress[] = [];

    for (const file of selectedFiles) {
      if (file.size > maxFileSizeBytes) {
        setErrorTitle("خطأ في حجم الملف");
        setErrorMessage("لا يمكن ان يتخطي حجم المرفق 6 mb");
        openpErrorModal();
        continue;
      }

      if (
        !file.type.match("image/jpeg") &&
        !file.type.match("image/jpg") &&
        !file.type.match("image/jpg") &&
        !file.type.match("image/jpe") &&
        !file.type.match("image/png") &&
        !file.type.match("image/gif") &&
        !file.type.match("video/*")
      ) {
        setErrorTitle("نوع ملف غير صحيح");
        setErrorMessage("يجب رفع صورة او فيديو");
        openpErrorModal();
        continue;
      }

      const fileWithProgress: FileWithProgress = {
        file,
        progress: 0,
        url: null,
        prepared: false,
      };

      const reader = new FileReader();

      const fileReadPromise = new Promise<{
        progress: number;
        url: string | null;
      }>((resolve, reject) => {
        reader.onprogress = (e) => {
          if (e.lengthComputable) {
            const progress = e.loaded / e.total;
            fileWithProgress.progress = progress;
          }
        };

        reader.onloadend = () => {
          const url = URL.createObjectURL(file);
          resolve({ progress: 1, url });
        };

        reader.onerror = () => {
          reject(new Error("File reading has failed"));
        };
      });

      reader.readAsDataURL(file);

      try {
        const { progress, url } = await fileReadPromise;
        fileWithProgress.progress = progress;
        fileWithProgress.url = url;
        fileWithProgress.prepared = true;
      } catch (error) {
        console.error("Error reading file:", error);
      }

      newFiles.push(fileWithProgress);
      console.log("fileWithProgress", fileWithProgress);
    }

    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
    console.log("handledFiles", files);

    // Reset the input value to allow selecting the same file again
    event.target.value = "";
  };

  const removeFile = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const validate = (values: any) => {
    let errors: any = {};

    if (!values.adLicenseNumber) {
      errors.adLicenseNumber = "يجب كتابة رقم ترخيص الاعلان";
    } else {
      // Convert to string to ensure startsWith method works
      const licenseNumber = String(values.adLicenseNumber);
      
      // Validate that adLicenseNumber starts with the correct prefix based on addIDType
      if (addIDType === "2" && !licenseNumber.startsWith("72")) {
        errors.adLicenseNumber = "يجب أن يبدأ رقم ترخيص الاعلان بـ 72 للمنشئات";
      } else if (addIDType === "1" && !licenseNumber.startsWith("71")) {
        errors.adLicenseNumber = "يجب أن يبدأ رقم ترخيص الاعلان بـ 71 للأفراد";
      }
    }
    
    // Validate CommRegistrationNo only when idType is "2"
    if (addIDType === "2" && !values.CommRegistrationNo) {
      errors.CommRegistrationNo = "يجب كتابة رقم السجل التجاري";
    }
    
    return errors;
  };

  function createFeatureGraphics(
    type: any,
    geometry: any,
    wiki: any,
    attr: any,
    symbolS: any
  ) {
    console.log("createFeatureGraphics ==>geometry", geometry);
    let fFeature: any;
    switch (type) {
      case "polygon":
        fFeature = new Polygon({
          rings: geometry,
          spatialReference: {
            wkid: wiki,
          },
        });
        break;
      case "point":
        fFeature = new Point({
          x: geometry.x,
          y: geometry.y,
          spatialReference: {
            wkid: wiki,
          },
        });
        break;
    }

    let fFeatureGraphic = new Graphic({
      geometry: fFeature,
      symbol: symbolS,
      attributes: attr,
    });
    return fFeatureGraphic;
  }

  function convertGeometryToGraphic(geometry: any, attributes: any) {
    setIsLoading(true);
    
    // Calculate centroid from polygon geometry
    const centroid = getPolygonCenterPoint(geometry as any);
    let x: number = centroid?.x || 0;
    let y: number = centroid?.y || 0;
    
    let pointGeometryG = {
      x: x,
      y: y,
    };
    let pPointgraphic: any = createFeatureGraphics(
      "point",
      pointGeometryG,
      4326,
      attributes,
      favoriteLandSymbol
    );
    console.log("pPointgraphic", pPointgraphic);
    return pPointgraphic;
  }

  // Generic helper to check if feature exists by geometry
  const checkFeatureExists = async (
    parcel_Cod: string,
    geometryRings: any
  ): Promise<{ exists: boolean; feature: any | null; existingStatuses: string[] }> => {
    // Create polygon geometry for spatial query
    const polygonGeometry = new Polygon({
      rings: geometryRings,
      spatialReference: { wkid: 4326 },
    });

    const query = new Query({
      returnGeometry: true,
      geometry: polygonGeometry,
      spatialRelationship: "intersects",
      outFields: ["*"],
      // Only the layer's BASE filter, never the home-map category filter: query
      // the service directly so an existing parcel is always found, even when
      // some categories are unchecked in the map filter. Otherwise the
      // "مبنى مختلط" (purple) decision on creation could be skipped.
      where: "Feature_Ad IS NULL AND (IsDeleted IS NULL OR IsDeleted <> 'true')",
    });

    const result = await executeQueryJSON(
      arcgisServicesLandFeatureUrl + "1",
      query
    );
    const existingStatuses: string[] = [];

    if (result.features.length > 0) {
      result.features.forEach((element: any) => {
        if (element.attributes.Parcel_Status) {
          existingStatuses.push(element.attributes.Parcel_Status);
        }
      });

      return {
        exists: true,
        feature: result.features[0],
        existingStatuses,
      };
    }

    return { exists: false, feature: null, existingStatuses: [] };
  };

  // Generic helper to update existing feature attributes
  const updateFeatureAttributes = async (
    feature: any,
    newAttributes: any,
    parcel_Status: string
  ): Promise<number> => {
    // Add BaseLandType to layer 1 attributes
    const typeId = getAdvertisementTypeId(parcel_Status);
    
    const updateFeature = {
      attributes: {
        OBJECTID: feature.attributes.OBJECTID,
        ...newAttributes,
        BaseLandType: typeId,
      },
    };

    const response = await window.F_Land_parcels.applyEdits({
      updateFeatures: [updateFeature],
    });

    if (response.updateFeatureResults[0].error != null) {
      throw new Error("فشل في تحديث البيانات الجغرافية");
    }

    window.F_Land_parcels.refresh();

    return feature.attributes.OBJECTID;
  };

  // Generic helper to update or add heatmap feature
  const updateOrAddHeatmapFeature = async (
    layerObjectId: number,
    parcel_Status: string,
    geometry: any,
    attributes: any
  ): Promise<void> => {
    // Create point geometry from the polygon for spatial query
    const centroid = getPolygonCenterPoint(geometry as any);
    const pointGeometry = new Point({
      x: centroid?.x || 0,
      y: centroid?.y || 0,
      spatialReference: { wkid: 4326 },
    });

    const query = new Query({
      returnGeometry: false,
      geometry: pointGeometry,
      spatialRelationship: "intersects",
      outFields: ["*"],
      // Query the heatmap service directly with its base filter only, so the
      // map category filter cannot hide an existing point and cause a duplicate
      // instead of an update.
      where: "(IsDeleted IS NULL OR IsDeleted <> 'true')",
    });

    const heatmapResult = await executeQueryJSON(
      arcgisServicesLandFeatureUrl + "2",
      query
    );

    const typeId = getAdvertisementTypeId(parcel_Status);
    attributes["BaseLandType"] = typeId;
    attributes["Parcel_Cod"] = layerObjectId; // Set to layer 1 OBJECTID

    if (heatmapResult.features.length > 0) {
      // Update existing heatmap feature
      const updateFeature = {
        attributes: {
          OBJECTID: heatmapResult.features[0].attributes.OBJECTID,
          BaseLandType: typeId,
          Parcel_Status: parcel_Status,
          Parcel_Cod: layerObjectId, // Update to match layer 1 OBJECTID
        },
      };

      const heatmapResponse = await window.F_Land_parcelsHeatmap.applyEdits({
        updateFeatures: [updateFeature],
      });

      if (heatmapResponse.updateFeatureResults[0].error == null) {
        window.F_Land_parcelsHeatmap.refresh();
      }
    } else {
      // Add new heatmap feature
      const pointGraphic = convertGeometryToGraphic(geometry, attributes);

      const heatmapResponse = await window.F_Land_parcelsHeatmap.applyEdits({
        addFeatures: [pointGraphic],
      });

      if (heatmapResponse.addFeatureResults[0].error == null) {
        window.F_Land_parcelsHeatmap.refresh();
      }
    }
  };

  // Generic helper to add land parcel feature
  const addLandParcelFeature = async (
    geometry: any,
    attributes: any,
    parcel_Status: string
  ): Promise<number> => {
    // Add BaseLandType to layer 1 attributes
    const typeId = getAdvertisementTypeId(parcel_Status);
    attributes["BaseLandType"] = typeId;
    
    const pPolygongraphic = createFeatureGraphics(
      "polygon",
      geometry,
      4326,
      attributes,
      selectedSymbolElshamalya
    );

    const response = await window.F_Land_parcels.applyEdits({
      addFeatures: [pPolygongraphic],
    });

    if (response.addFeatureResults[0].error != null) {
      throw new Error("فشل في إضافة البيانات الجغرافية");
    }

    window.F_Land_parcels.refresh();
    window.mapviews.goTo(pPolygongraphic);

    return response.addFeatureResults[0].objectId;
  };

  // Generic helper to add heatmap feature if needed
  const addHeatmapFeatureIfNeeded = async (
    layerObjectId: number,
    parcel_Status: string,
    geometry: any,
    attributes: any
  ): Promise<void> => {
    await updateOrAddHeatmapFeature(layerObjectId, parcel_Status, geometry, attributes);
  };

  // Updated function to create or update GIS features and return the objectId
  const createGisFeatures = async (
    parcel_Status: any,
    parcel_Cod: any,
    pointResult: PointDataResult
  ): Promise<number | null> => {
    console.log("createGisFeatures parcel_Cod", parcel_Cod);
    console.log("createGisFeatures parcel_Status", parcel_Status);
      const mixedBuilding = "مبنى مختلط"

    try {
      // Step 1: Check if feature already exists by geometry
      const { exists, feature, existingStatuses } = await checkFeatureExists(
        parcel_Cod,
        pointResult.geometry.rings
      );

      const finalStatus = determineParcelStatus(parcel_Status, existingStatuses);
      console.log("Final parcel status:", finalStatus);

      // Step 2: Prepare attributes (Parcel_Cod will be set after getting objectId)
      const attributes: any = {
        Parcel_Status: finalStatus,
        Land_Numbe: pointResult.parcelNo,
        NK: pointResult.city,
        DK: pointResult.districtName,
        ZK: pointResult.region,
      };

      let objectId: number;

      if (exists && feature) {
        // Step 3a: Update existing feature
        const existingStatus = feature.attributes.Parcel_Status;
        let statusToUpdate = finalStatus;

        if (existingStatus !== finalStatus) {
            statusToUpdate = mixedBuilding;
        }

        attributes.Parcel_Status = statusToUpdate;
        console.log("Feature exists, updating attributes...");
        console.log("Existing Status:", existingStatus);
        console.log("New Status:", finalStatus);
        console.log("Status to Update:", statusToUpdate);

        objectId = await updateFeatureAttributes(feature, attributes, statusToUpdate);
        
        // Update Parcel_Cod to match the objectId
        const updateParcelCod = {
          attributes: {
            OBJECTID: objectId,
            Parcel_Cod: objectId,
          },
        };
        
        await window.F_Land_parcels.applyEdits({
          updateFeatures: [updateParcelCod],
        });
        window.F_Land_parcels.refresh();
        
        presentToastFunction(
          presentToast,
          "top",
          "success",
          "تم تحديث البيانات الجغرافية بنجاح"
        );
      } else {
        // Step 3b: Add new feature
        console.log("Feature does not exist, creating new...");
        const rings = pointResult.geometry.rings;
        objectId = await addLandParcelFeature(rings, attributes, finalStatus);
        
        // Update Parcel_Cod to match the objectId
        const updateParcelCod = {
          attributes: {
            OBJECTID: objectId,
            Parcel_Cod: objectId,
          },
        };
        
        await window.F_Land_parcels.applyEdits({
          updateFeatures: [updateParcelCod],
        });
        window.F_Land_parcels.refresh();
        
        presentToastFunction(
          presentToast,
          "top",
          "success",
          "تم حفظ البيانات الجغرافية بنجاح"
        );
      }

      console.log("GIS ObjectId:", objectId);
      console.log("Parcel_Cod set to:", objectId);

      // Step 4: Update or add heatmap feature (use layer 1 OBJECTID for layer 2's Parcel_Cod)
      let heatmapStatus = finalStatus;
      if (exists && feature) {
          const existingStatus = feature.attributes.Parcel_Status;
          if (existingStatus !== finalStatus) {
              heatmapStatus = mixedBuilding;
          }
      }

      await addHeatmapFeatureIfNeeded(
        objectId,
        heatmapStatus,
        pointResult.geometry,
        attributes
      );

      return objectId;
    } catch (error) {
      console.log("createGisFeatures error", error);
      setErrorTitle("خطأ في البيانات الجغرافية");
      setErrorMessage("خطأ في حفظ البيانات الجغرافية");
      openpErrorModal();
      throw error;
    }
  };

  async function ApplyDataFromAQAR(values: any) {
    
    // Validate that all files are fully prepared
    if (files.length > 0 && files.some(f => !f.prepared)) {
      presentToastFunction(
        presentToast,
        "top",
        "warning",
        "يرجى الانتظار حتى انتهاء تحميل جميع المرفقات"
      );
      return;
    }
    
    setIsLoading(true);

    try {
      const similarAdsResponse = await GetSimilarAds(token, values.adLicenseNumber, undefined);
      if (similarAdsResponse.data && similarAdsResponse.data.length > 0) {
        setErrorTitle("عذرًا");
        setErrorMessage("هذا الإعلان موجود بالفعل");
        openpErrorModal();
        setIsLoading(false);
        return;
      }
    } catch (error) {
      console.error("Error checking similar ads:", error);
        setErrorTitle("خطأ");
        setErrorMessage("حدث خطأ اثناء التحقق من الوجود المسبق الإعلان");
        openpErrorModal();
        setIsLoading(false);
        return;
    }
    
    // Show initial progress feedback
    presentToastFunction(
      presentToast,
      "top",
      "primary",
      "جاري التحقق من البيانات..."
    );
    debugger
    console.log("addIDType", addIDType);
      let data: any = {
        IdType: addIDType,
        AdLicenseNumber: values.adLicenseNumber,
        CommRegistrationNo:
          addIDType == "2" && values.CommRegistrationNo !== "" ? values.CommRegistrationNo : "0",
      };
      const formData = new FormData();
    console.log("values", data);

      formData.append(`Data`, JSON.stringify(data) as any);
      if (values.file.length > 6) {
        setErrorTitle("عدد المرفقات كبير");
        setErrorMessage("عفوا لا يمكن رفع اكثر من 6 مرفقات");
        openpErrorModal();
        setIsLoading(false);
        return;
      }
      try {
        const ss = await CheckNhcIntegration(formData, token);
        console.log("CheckNhcIntegration", ss);
        if (ss && ss.data && ss.data.resultStatus == 200) {
          console.log("CheckNhcIntegration.data", ss.data);
          let result = ss.data.data.aNhcIntegrationResultDTO.body.result;
          console.log("CheckNhcIntegration.result", result);
          debugger

            let latitude = result.advertisement.location.latitude;
            let longitude = result.advertisement.location.longitude;
            if (
              latitude == null ||
              longitude == null ||
              latitude == "" ||
              longitude == ""
            ) {
              setErrorTitle("خطأ في البيانات الجغرافية");
              setErrorMessage("عفوا يرجي تحديث البيانات الجغرافية للاعلان الخاص بك من خلال موقع هيئة العقار");
              openpErrorModal();
              setIsLoading(false);
              return;
            }
            let propertyType = result.advertisement.propertyType;
            let advertisementType = result.advertisement.advertisementType;
            console.log("CheckNhcIntegration.data==> latitude", latitude);
            console.log("CheckNhcIntegration.data==> longitude", longitude);

            let objectId = "";
            const data = await getLandAdsByCoords(longitude, latitude);
            if (data[0].attributes.Parcel_Cod) {
              objectId = data[0].attributes.Parcel_Cod;
            }

            const baladyToken = await getBaladyAccessToken();
            console.log('Balady token obtained successfully for map initialization', baladyToken);
      
            const pointDataEnvelope: PointDataEnvelope = (
              await GetPointData(
              baladyToken,
              longitude,
              latitude
              )
            ).data;
            const pointResult = pointDataEnvelope.data.result;
            console.log("pointData", pointDataEnvelope);
            console.log("pointData result", pointResult);
            if (!pointResult.geometry) {
              setErrorTitle("خطأ في العثور على القطعة");
              setErrorMessage(`لا يمكن العثور علي القطعه المعلن عنها| {x: ${longitude}, y: ${latitude}} الرجاء المحاولة لاحقًا`);
              openpErrorModal();
              setIsLoading(false);
              return;
            }
            console.log("pointData", pointResult.geometry.rings);
            let neighbourhood_Name = pointResult.districtName ?? "";
            let districts_Name = pointResult.districtName ?? "";
            let city_Name = pointResult.city ?? "";
            objectId = pointResult.objectId;
            let landId = pointResult.parcelNo ?? "";
            let region = pointResult.region ?? "";

            const response = await GetTypeOfProperty(token, propertyType, advertisementType, values.AdLicenseNumber);
            let typeOfProperty = response?.data?.typeOfProperty;

            if (!typeOfProperty) {
              setErrorTitle("خطأ في تصنيف العقار");
              setErrorMessage("خطأ في معرفه تصنيف العقار المدخل");
              openpErrorModal();
              setIsLoading(false);
              return;
            }

            console.log("values", values);
            console.log("typeOfProperty", typeOfProperty);
            console.log("city_Name", city_Name);
            console.log("districts_Name", districts_Name);
            console.log("neighbourhood_Name", neighbourhood_Name);
              console.log("addIDType", addIDType);
              let dataToBePassed: any = {
                LandTypeDesc: typeOfProperty,
                IdType: addIDType == "" ? "1" : addIDType,
                AdLicenseNumber: values.adLicenseNumber,
                LandId: landId,
                objectId: objectId,
                CommRegistrationNo:
                  addIDType == "2" && values.CommRegistrationNo !== ""
                    ? values.CommRegistrationNo
                    : "0",
                Zone: neighbourhood_Name,
                City: city_Name,
                District: districts_Name,
                /*////////////////////////////////*/
                Region: region,
                description: values.description,
                /*////////////////////////////////*/
              };
              const formData = new FormData();
              if (values.file) {
                for (let i = 0; i < values.file.length; i++) {
                  let checkFileResult = await onFileUploadValidationSize(
                    values.file[i],
                    6 * 1048576
                  );
                  if (!checkFileResult) {
                    setErrorTitle("خطأ في حجم الملف");
                    setErrorMessage(` عفوا الملف  ${values.file[i].name}   حجم اكبر من 6 ميجا`);
                    openpErrorModal();
                    setIsLoading(false);
                    return;
                  }
                  const files: any = await convertToBase64(values.file[i]);
                  const regex = /data:.*base64,/;
                  const fileWithOut = files.replace(regex, "");

                  formData.append(
                    `AttachMents[${i}].File`,
                    fileWithOut as any
                  );
                  formData.append(
                    `AttachMents[${i}].Name`,
                    values.file[i].name as any
                  );
                }
              }

              formData.append(`Data`, JSON.stringify(dataToBePassed) as any);
              
              // Step 1: Create GIS features first and get objectId
              presentToastFunction(
                presentToast,
                "top",
                "primary",
                "جاري حفظ البيانات الجغرافية..."
              );
              
              let gisObjectId: number | null = null;
              
              try {
                gisObjectId = await createGisFeatures(
                  typeOfProperty,
                  objectId,
                  pointResult
                );
                
                if (!gisObjectId) {
                  throw new Error("فشل في الحصول على معرف الكائن الجغرافي");
                }
                
                console.log("GIS ObjectId obtained:", gisObjectId);
                
                // Step 2: Add gisObjectId to the data before sending to backend
                dataToBePassed.objectId = gisObjectId;
                dataToBePassed.parcel_Cod = gisObjectId;
                
                // Update formData with new data including GisObjectId
                formData.set(`Data`, JSON.stringify(dataToBePassed) as any);
                
                // Step 3: Call backend with the GIS objectId
                presentToastFunction(
                  presentToast,
                  "top",
                  "primary",
                  "جاري حفظ البيانات في الخادم..."
                );
                
                const backendResponse = await CreateNhcIntegration(formData, token);
                
                console.log("Backend response:", backendResponse);
                
                if (backendResponse && backendResponse.data.resultStatus == 200) {
                  console.log("CreateNhcIntegration.data", backendResponse.data);
                  presentToastFunction(
                    presentToast,
                    "top",
                    "success",
                    "تم الحفظ بنجاح"
                  );
                  setIsLoading(false);
                  dismisAddProperty(null, "cancel");
                } else {
                  // Backend failed, rollback GIS data
                  const parsed = JSON.parse(backendResponse?.data?.message || "{}");
                  const errors = Array.isArray(parsed?.errors) && parsed?.errors?.length > 0
                    ? parsed?.errors?.join(", ")
                    : "";
                  const message = errors + " | " + parsed?.message;
                  
                  setErrorTitle("فشل حفظ البيانات داخل الخادم");
                  setErrorMessage(message);
                  openpErrorModal();
                  
                  // Rollback or update GIS data based on backend ads
                  await rollbackGisData(+gisObjectId, values.adLicenseNumber);
                }
                
              } catch (error: any) {
                console.error("Error in create process:", error);
                
                // Rollback or update GIS if it was created
                if (gisObjectId) {
                  await rollbackGisData(+gisObjectId, values.adLicenseNumber);
                }
                
                setIsLoading(false);
                
                if (error.response?.status == 401) {
                  window.localStorage.removeItem("persistantState");
                  window.location.assign(`/app/signIn`);
                  return;
                }
                
                setErrorTitle("خطأ في حفظ البيانات داخل الخادم");
                setErrorMessage(error?.message || "حدث خطأ في حفظ البيانات");
                openpErrorModal();
              }
          } else {
            setIsLoading(false);
            const parsed = JSON.parse(ss?.data?.message || "{}");
            const errors = Array.isArray(parsed.errors) && parsed.errors.length > 0
              ? parsed.errors.join(", ")
              : "";
            const message = errors + " | " + parsed.message;

            setErrorTitle("خطأ في التحقق");
            setErrorMessage(message);
            openpErrorModal();
          }
      } catch (e: any) {
          console.log(e);
          // if (e.response?.status == 401) {
          //   window.localStorage.removeItem("persistantState");
          //   window.location.assign(`/app/signIn`);
          // }
          setIsLoading(false);
          setErrorTitle("حدث خطأ");
          setErrorMessage(e?.message ??
            "لم تتم عملية الإضافة .. تأكد من المدخلات! أو أعد المحاولة"
          );
          openpErrorModal();
      }
  }

  //model property
  const [presentErrorModal, dismissErrorModal] = useIonModal(ErrorModal, {
    errorTitle: errorTitle,
    errorMessage: errorMessage,
    dismissErrorModal: (data: string, role: string) =>
      dismissErrorModal(data, role),
  });

  function openpErrorModal() {
    presentErrorModal({
      animated: true,
      breakpoints: [1, 1, 1, 0.5, 1],
      initialBreakpoint: 0.5,
      // backdropBreakpoint: 0.5,
      onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
        if (ev.detail.role === "confirm") {
        }
      },
    });
  }

  // Edit Profile Modal
  const [editProfilePresent, editProfileDismiss] = useIonModal(EditProfile, {
    editProfileDismiss: (data: string, role: string) => {
      editProfileDismiss(data, role);
      // Recheck phone number after modal dismisses
      if (!userInfo?.phoneNumber || userInfo.phoneNumber === "") {
        setShowPhoneWarning(true);
      } else {
        setShowPhoneWarning(false);
      }
    },
  });

  function openEditProfileModal() {
    setShowPhoneWarning(false);
    editProfilePresent({
      onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
        if (ev.detail.role === "confirm") {
        }
      },
    });
  }

  /*//////////////////////////////////////////////////////////////////////////*/

  let getIntersectWithGraphics = async (geometry: any, url: string) => {
    let query = new Query({
      returnGeometry: true,
      geometry: geometry,
      outFields: ["*"],
      spatialRelationship: "intersects",
    });
    let featureLayer = new FeatureLayer({
      url: url,
      outFields: ["*"],
    });
    let intersectingGraphics = await featureLayer.queryFeatures(query);
    return intersectingGraphics.features;
  };
  function featureGeometry(type: any, features: any, wKid: any) {
    let obj: any = {
      type: type,
      spatialReference: {
        wkid: wKid,
      },
    };
    switch (type) {
      case "polyline":
        obj["paths"] = features;
        break;
      case "polygon":
        obj["rings"] = features;
    }
    return obj;
  }

  return (
    <IonPage>
      <IonContent>
        {/* Phone Number Warning Modal */}
        {showPhoneWarning && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{
              backdropFilter: "blur(8px)",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
            }}
          >
            <div className="bg-white rounded-2xl shadow-2xl p-8 mx-6 max-w-md w-full animate__animated animate__zoomIn">
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <svg 
                    className="w-12 h-12 text-red-600" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                    />
                  </svg>
                </div>
                
                <h2 className="text-2xl font-bold text-gray-800 mb-3">
                  رقم الهاتف غير موجود
                </h2>
                
                <p className="text-lg text-gray-600 mb-6">
                  يجب عليك إضافة رقم الهاتف الخاص بك قبل المتابعة. الرجاء الانتقال إلى صفحة تعديل الملف الشخصي لإضافة رقم هاتفك.
                </p>
                
                <div className="w-full space-y-3">
                  <button
                    onClick={openEditProfileModal}
                    className="w-full bg-primaryColor100 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                  >
                    انتقل إلى تعديل الملف الشخصي
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowPhoneWarning(false);
                      dismisAddProperty(null, "cancel");
                    }}
                    className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                  >
                    إلغاء
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="w-full flex flex-col justify-start items-center bg-white">
          <div
            className={`w-full grid grid-cols-3 bg-primaryColor100 rounded-lg ${isPlatform("ios") && "pt-7"
            }`}
          >
            <div className="col-span-1"></div>
            <div
              className={`col-span-1 ${isPlatform("ios") ? "py-6" : "py-4"
              } flex flex-row justify-center items-center`}
            >
              <p className="font-bold text-white text-2xl whitespace-nowrap">
                اضافة اعلان جديد
              </p>
            </div>
            <div
              className={`col-span-1 ${isPlatform("ios") ? "py-6" : "py-4"
              } px-6 flex flex-row justify-end items-center`}
            >
              <button
                onClick={() => {
                  dismisAddProperty(null, "cancel");
                }}
                className="flex flex-col justify-center items-center"
              >
                <IonIcon
                  icon={chevronBackOutline}
                  className="text-2xl text-white"
                />
              </button>
            </div>
          </div>
          <Formik
            innerRef={formikRef}
            initialValues={{
              adLicenseNumber: "",
              idType: addIDType,
              parcel_Cod: 0,
              landTypeDesc: "",
              LandId: "",
              objectId: 0,
              CommRegistrationNo: "",
              Zone: "",
              City: "",
              District: "",
              discription: "",
            }}
            validateOnBlur={true}
            validateOnChange={true}
            onSubmit={async (values: any, actions) => {
              if (isSubmittingRef.current) return;
              isSubmittingRef.current = true;
              try {
                values.file = files.map((e) => e.file);
                await ApplyDataFromAQAR(values);
              } finally {
                isSubmittingRef.current = false;
              }
            }}
            validate={validate}
          >
            {({ handleSubmit, errors, touched, isValid, dirty }) => (
              <div className="w-full h-full py-2 px-8 bg-white flex flex-col justify-start items-center">
                {isLoading ? (
                  <Spinner color={"primary"} size={"large"} />
                ) : null}
                <div className="w-full flex flex-col justify-start items-start mt-4 animate__animated animate__fadeInBottomRight">
                  <div className="w-full flex justify-between items-center">
                    <p className="font-medium text-black text-2xl">
                      بيانات الاعلان
                    </p>
                    <div className={`px-4 py-2 rounded-lg font-bold text-lg ${
                      addIDType === "1" 
                        ? "bg-blue-100 text-blue-700 border-2 border-blue-300" 
                        : "bg-green-100 text-green-700 border-2 border-green-300"
                    }`}>
                      {addIDType === "1" ? "فرد" : "منشأة"}
                    </div>
                  </div>
                </div>

                <div className="relative w-full mt-2">
                  <IonList className="animate__animated animate__fadeInBottomRight">
                    <IonRadioGroup
                      value={addIDType}
                      className="flex flex-nowrap"
                      onIonChange={(ev) => {
                        setAddIDType(ev.detail.value);
                      }}
                    >
                      <IonItem className="w-40">
                        <IonRadio key={1} value={"1"}></IonRadio>
                        <label className="mr-2 text-xl">رقم الهوية</label>
                      </IonItem>
                      <IonItem className="w-60">
                        <IonRadio key={2} value={"2"}>
                          {" "}
                        </IonRadio>
                        <label className="mr-2 text-xl">
                          رقم الموحد للمنشات
                        </label>
                      </IonItem>
                    </IonRadioGroup>
                  </IonList>
                </div>

                {addIDType == "2" ? (
                  <>
                    <div className="relative w-full mt-6">
                      <Field
                        type="number"
                        name="CommRegistrationNo"
                        disabled={false}
                        className={`${inputClassWithOutError}`}
                      />
                      <label className="absolute text-xl font-bold top-2 z-10 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                        رقم السجل التجاري <span className="text-redColor">*</span>
                      </label>
                      
                      {/* Error message below input */}
                      {errors.CommRegistrationNo && touched.CommRegistrationNo && (
                        <p className="text-redColor text-base mt-1 mr-2">
                          {errors.CommRegistrationNo}
                        </p>
                      )}
                    </div>
                  </>
                ) : null}

                <div className="relative w-full mt-6 animate__animated animate__fadeInBottomLeft">
                  <Field
                    type="number"
                    name="adLicenseNumber"
                    disabled={false}
                    className={`${inputClassWithOutError}`}
                  />
                  <label className="absolute text-xl font-bold top-2 z-10 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                    رقم ترخيص الاعلان <span className="text-redColor">*</span>
                  </label>
                  
                  {/* Error message below input */}
                  {errors.adLicenseNumber && touched.adLicenseNumber && (
                    <p className="text-redColor text-base mt-1 mr-2">
                      {errors.adLicenseNumber}
                    </p>
                  )}

                  {addIDType == "2" ? (
                    <>
                      <div className="relative w-full mt-6">
                        <label className="absolute text-xl font-bold z-10 bg-white text-paragraphColor -translate-y-6 ">
                          يبدأ ب 72
                        </label>
                      </div>
                    </>
                  ) : (
                    <div className="relative w-full mt-6">
                      <label className="absolute text-xl font-bold z-10 bg-white text-paragraphColor -translate-y-6 ">
                        يبدأ ب 71
                      </label>
                    </div>
                  )}
                </div>

                <div className="w-full flex flex-col justify-start items-start mt-4 animate__animated animate__bounceInUp">
                  <p className="font-medium text-black text-2xl">
                    رفع المرفقات <span className="text-paragraphColor">(اختيارى)</span>
                  </p>
                  <span className="text-paragraphColor mr-2">
                    يمكن الرفع الى 6 مرفقات
                  </span>
                </div>
                <div className="w-full overflow-x-auto mt-2">
                  <div className="flex space-x-4">
                    {files.map((fileWithProgress, index) => (
                      <div
                        key={index}
                        className="relative flex-shrink-0 mx-1 border-2 border-solid border-paragraphColor rounded-lg"
                      >
                        {fileWithProgress.file.type.startsWith("image/") ? (
                          <img
                            src={fileWithProgress.url || ""}
                            alt="thumbnail"
                            className="w-28 h-28 object-cover"
                          />
                        ) : (
                          <video
                            src={fileWithProgress.url || ""}
                            controls
                            className="w-28 h-28 object-cover"
                          />
                        )}
                        <button
                          onClick={() => {
                            removeFile(index);
                          }}
                          className="absolute top-0 right-0 rounded-full p-1 border-1 text-xl"
                        >
                          <IonIcon
                            className="bg-whiteBlueColor rounded"
                            icon={deleteSvg}
                          />
                        </button>

                        {!fileWithProgress.prepared && (
                          <IonProgressBar
                            value={fileWithProgress.progress}
                            className="absolute bottom-0 left-0 right-0"
                          />
                        )}
                      </div>
                    ))}
                    {files.length < 6 && (
                      <div className="w-28 h-28 border-2 border-dotted border-paragraphColor rounded-md flex items-center justify-center flex-shrink-0 animate__animated animate__bounceInUp">
                        <input
                          type="file"
                          multiple
                          accept="image/*, video/*"
                          onChange={(event) => {
                            handleFileChange(event);
                            // setFieldValue("file", files.map((e) => e.file))
                          }}
                          className="opacity-0"
                          style={{ width: "40px", marginLeft: "-35px" }}
                        />
                        <span className="text-6xl text-paragraphColor">+</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="relative w-full mt-6">
                  <Field
                    as="textarea"
                    name="description"
                    disabled={false}
                    rows={6}
                    className={`${inputClassWithOutError}`}
                  />
                  <label className="absolute text-xl font-bold top-2 z-10 bg-white px-2 text-primaryColor100 -translate-y-6 right-10">
                    الوصف <span className="text-paragraphColor">(اختيارى)</span>
                  </label>
                </div>

                <div className="w-full my-6 animate__animated animate__bounceInUp">
                  <Button
                    theme={"primary"}
                    text={"اضافة اعلانك"}
                    size={"big"}
                    onClick={handleSubmit}
                    isLoading={isLoading}
                    disabled={isLoading || !isValid || !dirty}
                  />
                </div>
              </div>
            )}
          </Formik>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AddPropertyNhcIntegration;
